import Container from "@/components/Container";
import useMediaQuery from "@/custom_hooks/mediaQuery";
import { FC, useState } from "react";
import ActionLink from "@/components/UI/Buttons/ActionLink";
import HealthProblems from "@/components/HealthProblems/HealthProblems";
import FAQSection from "@/components/FAQSection";
import { TEAM_CONTENT_VALUES, TeamMember } from "@/components/Team/constants";
import { Link } from "react-router-dom";
import ROUTES from "@/data/Routes";
import { COLLAPSABLE_CONTENT_HOME_FAQ_VALUES } from "@/components/Collapsible/constants";
import ClientsSay from "@/components/ClientsSay";
import { Button } from "@/components/UI/Buttons/Button";

const TeamList: FC = () => {
  const isMobile = useMediaQuery(768);
  const [isExpanded, setIsExpanded] = useState(false);
  const initialVisibleCount = isMobile ? 4 : 6;

  const visibleTeamMembers = isExpanded
    ? TEAM_CONTENT_VALUES
    : TEAM_CONTENT_VALUES.slice(0, initialVisibleCount);

  return (
    <div className="flex flex-col bg-white">
      <section className="flex flex-col px-4 pt-25 pb-7.5 md:px-6 md:pt-50 md:pb-12.5">
        <Container>
          <div className="flex flex-col items-center justify-center gap-5 text-center">
            <h1 className="text-ink text-[34px] leading-tight font-light md:text-[44px]">
              Patyrusių specialistų komanda
            </h1>

            <div className="text-muted max-w-[652px] text-base leading-relaxed md:text-lg">
              <p>
                Telomeros misija yra tirti, kurti ir tiekti pažangiausias
                terapijas, kurios sprendžia pagrindines ligų priežastis,
                siekiant užtikrinti sveikesnę ateitį visiems.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="flex flex-col px-4 md:px-6">
        <Container>
          <div className="grid grid-cols-2 gap-5 md:grid-cols-3">
            {visibleTeamMembers.map((member) =>
              member.content ? (
                <Link
                  key={member.id}
                  to={`/komanda/${member.id}`}
                  className="group border-stroke rounded-card flex flex-col gap-5 overflow-hidden border bg-white transition-colors hover:border-gray-300"
                >
                  <MemberCardContent member={member} />
                </Link>
              ) : (
                <div
                  key={member.id}
                  className="border-stroke rounded-card flex flex-col gap-5 overflow-hidden border bg-white"
                >
                  <MemberCardContent member={member} />
                </div>
              ),
            )}
          </div>

          {!isExpanded && TEAM_CONTENT_VALUES.length > initialVisibleCount ? (
            <div className="flex flex-10 justify-center pt-7.5 md:pt-12.5">
              <Button
                type="button"
                variant="white"
                className="bg-paper border-stroke h-[52px] w-[271px] py-[14px] text-[16px] font-normal md:h-[56px]"
                onClick={() => setIsExpanded(true)}
                onHover
                hoverDark
              >
                Rodyti daugiau
              </Button>
            </div>
          ) : null}
        </Container>
      </section>

      <section className="flex justify-center px-4 pt-7.5 pb-7.5 md:px-6 md:pt-12.5 md:pb-25">
        <Container className="flex justify-center">
          <ActionLink
            to={ROUTES.CONSULTATION}
            variant="dark"
            className="w-auto min-w-[271px]"
            hoverGradientLight
            onHover
          >
            Registruotis konsultacijai
          </ActionLink>
        </Container>
      </section>

      <HealthProblems />

      <section className="flex flex-col bg-white">
        <div className="w-full">
          <ClientsSay isMobile={isMobile} />
        </div>
      </section>

      <FAQSection data={COLLAPSABLE_CONTENT_HOME_FAQ_VALUES.items} />
    </div>
  );
};

export default TeamList;

const MemberCardContent: FC<{ member: TeamMember }> = ({ member }) => (
  <>
    <div className="rounded-card relative overflow-hidden">
      <img
        src={member.img_src}
        alt={member.alt}
        className="h-[150px] w-full object-cover object-top grayscale md:h-75"
      />
    </div>
    <div className="flex flex-col gap-3 px-5 pb-5">
      <p className="text-ink text-xl leading-tight font-medium md:text-[24px]">
        {member.name}
      </p>
      <p className="text-muted text-base leading-relaxed">{member.position}</p>
    </div>
  </>
);
